import { useMemo } from 'react';

import { IncidentDetailsPanel } from '@/components/incident';
import { IncidentSceneGrouped } from '@/components/subjects';
import { Skeleton } from '@/components/ui/skeleton';
import { enhancedApi as firmsApi } from '@/services/api/generated/firmsService';
import { useGetIncidentsQuery } from '@/services/api/incidentService';
import { mattersApiClient } from '@/services/api/mattersService';
import { usePoliceReportsListQuery } from '@/services/api/policeReportsService';
import { type MatterClient } from '@/services/types/matter-client-types';

import { InFlightRequestsTable } from './InFlightRequestsTable';
import { InvestigationPizzaTracker } from './InvestigationPizzaTracker';
import { PoliceContent } from './PoliceContent';

interface PoliceOverviewMainContentProps {
  matterId: string;
  onReportSelect: (reportId: string) => void;
  onCreateReport: () => void;
}

const IncidentFormSkeleton = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <Skeleton className="h-5 w-32 mb-4" />
    <div className="space-y-3">
      <div>
        <Skeleton className="h-4 w-24 mb-2" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div>
        <Skeleton className="h-4 w-28 mb-2" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div>
        <Skeleton className="h-4 w-36 mb-2" />
        <Skeleton className="h-24 w-full" />
      </div>
    </div>
  </div>
);

const IncidentSceneSkeleton = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <Skeleton className="h-5 w-32 mb-4" />
    <div className="space-y-3">
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-20 w-full" />
    </div>
  </div>
);

export const PoliceOverviewMainContent = ({
  matterId,
  onReportSelect,
  onCreateReport,
}: PoliceOverviewMainContentProps) => {
  // Fetch police reports (lightweight - no automation history)
  const {
    data: policeReports,
    isLoading: isPoliceReportsLoading,
    error: policeReportsError,
    refetch: refetchPoliceReports,
  } = usePoliceReportsListQuery({ matterId }, { skip: !matterId });

  // Fetch matter details (includes firm and client data)
  const { data: matter, isLoading: isMatterLoading } =
    mattersApiClient.useGetMatterQuery(matterId, { skip: !matterId });

  // Fetch firm service configuration
  const { data: firmServices, isLoading: isFirmServicesLoading } =
    firmsApi.useGetFirmServicesQuery(
      { firmId: matter?.firm.id || '' },
      { skip: !matter?.firm.id },
    );

  // Fetch incident data
  const { data: incidents, isLoading: isIncidentsLoading } =
    useGetIncidentsQuery({ matterId }, { skip: !matterId });

  const incident = incidents?.[0]; // 1-to-1 relationship
  const client: MatterClient | undefined = matter?.clients?.[0]; // Get first client

  // Check if firm requires FOIA requests (ALWAYS policy)
  const firmRequiresFoia = useMemo(() => {
    if (!firmServices) return false;
    const policeService = firmServices.find(
      (service) => service.service.name === 'police_reports',
    );
    return (
      policeService?.config &&
      'send_foia_request_to_pd' in policeService.config &&
      policeService.config.send_foia_request_to_pd === 'ALWAYS'
    );
  }, [firmServices]);

  const isDataLoading =
    isMatterLoading ||
    isFirmServicesLoading ||
    isIncidentsLoading ||
    isPoliceReportsLoading;

  return (
    <div className="flex-1 p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Investigation Status */}
        <InvestigationPizzaTracker
          matterId={matterId}
          policeReports={policeReports || []}
          dateOfLoss={incident?.date_of_loss}
          isLoading={isDataLoading}
        />

        {/* In-flight requests */}
        <InFlightRequestsTable matterId={matterId} />

        {/* Involved Agencies & Reports */}
        <PoliceContent
          isLoading={isPoliceReportsLoading}
          error={!!policeReportsError}
          policeReports={policeReports || []}
          matterId={matterId}
          onReportSelect={onReportSelect}
          onCreateReport={onCreateReport}
          onRefetch={refetchPoliceReports}
          firmRequiresFoia={firmRequiresFoia || undefined}
        />

        {/* Incident Note and Scene */}
        {isDataLoading ? (
          <div className="grid grid-cols-2 gap-6">
            <IncidentFormSkeleton />
            <IncidentSceneSkeleton />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <IncidentDetailsPanel matterId={matterId} incident={incident} />
            </div>

            <div>
              <IncidentSceneGrouped matterId={matterId} client={client} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
